import React, { useState } from 'react';
import {
  ShieldCheck,
  CheckCircle2,
  UserCheck,
  MapPin,
  Award,
  Clock,
  AlertCircle,
  ShieldX,
  Lock,
  ChevronDown,
  Sparkles,
  Info,
} from 'lucide-react';
import { Profile, VerificationStatus, KYCOverallStatus, PublicVerificationBadges } from '../../types';

export interface VerifiedBadgeProps {
  /**
   * Profile object or individual verification fields from database
   */
  profile?: Profile | any;
  status?: VerificationStatus;
  kycStatus?: KYCOverallStatus;
  badges?: PublicVerificationBadges;
  isProfilePhotoVerified?: boolean;
  
  /**
   * Layout Variant
   * - 'pill': Compact badge pill (default for cards)
   * - 'icon_only': Small verified shield overlay for avatars
   * - 'detailed': Expanded list of verified trust signals with sub-pills
   * - 'banner': Full banner mode with Trust & Safety guarantees
   */
  variant?: 'pill' | 'icon_only' | 'detailed' | 'banner';
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  interactivePopover?: boolean;
  className?: string;
}

export const VerifiedBadge: React.FC<VerifiedBadgeProps> = ({
  profile,
  status: propStatus,
  kycStatus: propKycStatus,
  badges: propBadges,
  isProfilePhotoVerified: propPhotoVerified,
  variant = 'pill',
  size = 'sm',
  showLabel = true,
  interactivePopover = true,
  className = '',
}) => {
  const [showPopover, setShowPopover] = useState<boolean>(false);

  // Extract authoritative status fields from profile or props
  const status: VerificationStatus = profile?.verificationStatus || propStatus || 'pending';
  const kycStatus: KYCOverallStatus | undefined = profile?.kycStatus || propKycStatus;
  const badges: PublicVerificationBadges | undefined = profile?.verificationBadges || propBadges;
  const isPhotoVerified = Boolean(
    profile?.isProfilePhotoVerified ??
    profile?.is_profile_photo_verified ??
    propPhotoVerified ??
    badges?.faceVerified
  );

  // Computed status flags
  const isFullyVerified =
    badges?.isFullyVerified ||
    kycStatus === 'VERIFIED' ||
    status === 'verified';

  const isPartiallyVerified =
    !isFullyVerified &&
    (badges?.identityVerified ||
      badges?.addressVerified ||
      badges?.locationVerified ||
      kycStatus === 'ID_VERIFIED' ||
      kycStatus === 'ADDRESS_VERIFIED' ||
      kycStatus === 'LOCATION_VERIFIED' ||
      kycStatus === 'FACE_VERIFIED' ||
      isPhotoVerified);

  const isSuspended = kycStatus === 'SUSPENDED' || status === 'suspended';
  const isRejected = kycStatus === 'REJECTED' || status === 'rejected';
  const isResubmission =
    kycStatus === 'RESUBMISSION' ||
    kycStatus === 'ACTION_REQUIRED' ||
    status === 'resubmission';
  const isUnderReview =
    kycStatus === 'UNDER_REVIEW' ||
    kycStatus === 'PENDING_REVIEW' ||
    status === 'under_review';
  const isExpired = kycStatus === 'EXPIRED';

  // Sub-verification indicators from database
  const identityVerified = Boolean(badges?.identityVerified || badges?.governmentIdVerified || isFullyVerified || kycStatus === 'ID_VERIFIED');
  const addressVerified = Boolean(badges?.addressVerified || isFullyVerified || kycStatus === 'ADDRESS_VERIFIED');
  const faceVerified = Boolean(badges?.faceVerified || isPhotoVerified || isFullyVerified || kycStatus === 'FACE_VERIFIED');
  const locationVerified = Boolean(badges?.locationVerified || isFullyVerified || kycStatus === 'LOCATION_VERIFIED');
  const tradeVerified = Boolean(badges?.tradeCertVerified || (profile?.tradeCertifications && profile.tradeCertifications.length > 0));

  // Size styling maps
  const sizeClasses = {
    xs: 'px-1.5 py-0.5 text-[10px]',
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-2.5 py-1 text-xs',
    lg: 'px-3.5 py-1.5 text-sm',
  };

  const iconSizes = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  // ---------------------------------------------------------------------------
  // Variant 1: Avatar Overlay Icon Only
  // ---------------------------------------------------------------------------
  if (variant === 'icon_only') {
    if (!isFullyVerified && !isPartiallyVerified) return null;

    return (
      <div className={`relative inline-block ${className}`}>
        <span
          className={`flex items-center justify-center rounded-full bg-emerald-600 text-white ring-2 ring-white shadow-xs ${
            size === 'xs' ? 'w-4 h-4' : size === 'sm' ? 'w-5 h-5' : size === 'lg' ? 'w-7 h-7' : 'w-6 h-6'
          }`}
          title={isFullyVerified ? 'Verified Artisan (NIMC NIN & Liveness Vetted)' : 'Verified Identity'}
        >
          <ShieldCheck className={iconSizes[size]} />
        </span>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Suspended Status Badge
  // ---------------------------------------------------------------------------
  if (isSuspended) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 font-extrabold rounded-full bg-rose-50 text-rose-800 border border-rose-300 ${sizeClasses[size]} ${className}`}
        title="Account suspended by Trust & Safety Desk"
      >
        <ShieldX className={`${iconSizes[size]} text-rose-600`} />
        {showLabel && <span>Account Suspended</span>}
      </span>
    );
  }

  // ---------------------------------------------------------------------------
  // Expired Status Badge
  // ---------------------------------------------------------------------------
  if (isExpired) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 font-extrabold rounded-full bg-amber-50 text-amber-800 border border-amber-300 ${sizeClasses[size]} ${className}`}
        title="Annual verification expired. Re-verification required."
      >
        <Clock className={`${iconSizes[size]} text-amber-600`} />
        {showLabel && <span>Verification Expired</span>}
      </span>
    );
  }

  // ---------------------------------------------------------------------------
  // Rejected Status Badge
  // ---------------------------------------------------------------------------
  if (isRejected) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 font-extrabold rounded-full bg-rose-50 text-rose-700 border border-rose-200 ${sizeClasses[size]} ${className}`}
        title="Identity verification declined"
      >
        <AlertCircle className={`${iconSizes[size]} text-rose-600`} />
        {showLabel && <span>Unverified</span>}
      </span>
    );
  }

  // ---------------------------------------------------------------------------
  // Resubmission / Under Review / Pending Badges
  // ---------------------------------------------------------------------------
  if (isResubmission || isUnderReview || (!isFullyVerified && !isPartiallyVerified)) {
    return (
      <span
        className={`inline-flex items-center gap-1.5 font-bold rounded-full bg-slate-100 text-slate-700 border border-slate-200/80 ${sizeClasses[size]} ${className}`}
        title={isUnderReview ? 'Identity verification under compliance review' : 'Verification Pending'}
      >
        <Clock className={`${iconSizes[size]} text-slate-500`} />
        {showLabel && <span>{isUnderReview ? 'Under Review' : 'Verification Pending'}</span>}
      </span>
    );
  }

  // ---------------------------------------------------------------------------
  // Fully Verified or Partially Verified Badge
  // ---------------------------------------------------------------------------
  return (
    <div className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={() => interactivePopover && setShowPopover(!showPopover)}
        onMouseEnter={() => interactivePopover && setShowPopover(true)}
        onMouseLeave={() => interactivePopover && setShowPopover(false)}
        className={`inline-flex items-center gap-1.5 font-black rounded-full transition-all cursor-pointer ${
          isFullyVerified
            ? 'bg-emerald-50 hover:bg-emerald-100 text-emerald-900 border border-emerald-300/90 shadow-2xs'
            : 'bg-teal-50 hover:bg-teal-100 text-teal-900 border border-teal-300'
        } ${sizeClasses[size]}`}
      >
        <ShieldCheck
          className={`${iconSizes[size]} ${
            isFullyVerified ? 'text-emerald-600 fill-emerald-600/20' : 'text-teal-600'
          }`}
        />
        {showLabel && (
          <span className="flex items-center gap-1">
            <span>{isFullyVerified ? 'Verified Pro' : 'ID Verified'}</span>
            {interactivePopover && (
              <ChevronDown className="w-2.5 h-2.5 text-emerald-700 opacity-60" />
            )}
          </span>
        )}
      </button>

      {/* Interactive Verification Popover Tooltip */}
      {showPopover && interactivePopover && (
        <div className="absolute left-0 top-full mt-1.5 z-50 w-72 p-4 bg-slate-900 text-white rounded-2xl shadow-2xl border border-slate-700 text-xs animate-in fade-in space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-slate-800">
            <div className="flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-emerald-400" />
              <span className="font-extrabold text-white text-xs">
                {isFullyVerified ? 'Verified Artisan Credentials' : 'Partial Identity Verification'}
              </span>
            </div>
            <span className="px-1.5 py-0.5 rounded text-[10px] font-black bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
              JOBMATCH VETTED
            </span>
          </div>

          <p className="text-[11px] text-slate-300 leading-relaxed">
            This professional has submitted official government identity & location credentials verified in the JobMatch database:
          </p>

          <div className="space-y-2">
            {/* NIMC / Govt ID */}
            <div className="flex items-center justify-between p-2 rounded-xl bg-slate-800/80 border border-slate-700/60">
              <div className="flex items-center gap-2">
                <UserCheck className={`w-3.5 h-3.5 ${identityVerified ? 'text-emerald-400' : 'text-slate-500'}`} />
                <span className="text-[11px] font-bold text-slate-200">
                  {profile?.idType ? `Govt ID (${profile.idType.split(' ')[0]})` : 'NIMC NIN / Govt ID'}
                </span>
              </div>
              <span className={`text-[10px] font-extrabold ${identityVerified ? 'text-emerald-400' : 'text-slate-500'}`}>
                {identityVerified ? 'Verified ✓' : 'Pending'}
              </span>
            </div>

            {/* Biometric Liveness / Photo */}
            <div className="flex items-center justify-between p-2 rounded-xl bg-slate-800/80 border border-slate-700/60">
              <div className="flex items-center gap-2">
                <Sparkles className={`w-3.5 h-3.5 ${faceVerified ? 'text-emerald-400' : 'text-slate-500'}`} />
                <span className="text-[11px] font-bold text-slate-200">Biometric Selfie Match</span>
              </div>
              <span className={`text-[10px] font-extrabold ${faceVerified ? 'text-emerald-400' : 'text-slate-500'}`}>
                {faceVerified ? 'Verified ✓' : 'Pending'}
              </span>
            </div>

            {/* Address & LGA Location */}
            <div className="flex items-center justify-between p-2 rounded-xl bg-slate-800/80 border border-slate-700/60">
              <div className="flex items-center gap-2">
                <MapPin className={`w-3.5 h-3.5 ${addressVerified || locationVerified ? 'text-emerald-400' : 'text-slate-500'}`} />
                <span className="text-[11px] font-bold text-slate-200">LGA & Address Proof</span>
              </div>
              <span className={`text-[10px] font-extrabold ${addressVerified || locationVerified ? 'text-emerald-400' : 'text-slate-500'}`}>
                {addressVerified || locationVerified ? 'Verified ✓' : 'Pending'}
              </span>
            </div>

            {/* Trade Certification */}
            {tradeVerified && (
              <div className="flex items-center justify-between p-2 rounded-xl bg-slate-800/80 border border-slate-700/60">
                <div className="flex items-center gap-2">
                  <Award className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-[11px] font-bold text-slate-200">Trade Certification</span>
                </div>
                <span className="text-[10px] font-extrabold text-amber-400">Certified ✓</span>
              </div>
            )}
          </div>

          <div className="pt-1 text-[10px] text-slate-400 flex items-center justify-between border-t border-slate-800/80">
            <span className="flex items-center gap-1">
              <Lock className="w-3 h-3 text-emerald-400" />
              <span>JobMatch Protection Guarantee</span>
            </span>
            <span>100% Secure</span>
          </div>
        </div>
      )}
    </div>
  );
};
